import {
  observable,
  action,
  computed,
  toJS,
} from 'mobx';
import debounce from 'lodash/debounce';
import { getI18n } from 'react-i18next';
import { showConfirmModal } from '../modules/modals/confirmService';
import DEFAULT_GUI from '../constants/DefaultGUI';
import DEFAULT_NON_DEVICE_GUI from '../constants/DefaultNonDeviceGUI';
import BATCH_TYPE_ENUMS, {
  IconDirection,
  MixerType,
  MixingStartPoint,
} from '../constants/BatchTypeEnums';
import {
  AnalogIconList,
  AnalogUnitList,
  BaudRate,
  DeviceModels,
  DeviceTypes,
  DisplayMode,
  DoubleGatesOpenMode,
  IconBinList,
  IconList,
  Parity,
  ReorderLevelType,
  SecondGateOpenDelayUnit,
  VibratorMode,
} from '../constants/DeviceModels';
import logger from '../utils/logger';
import shortid from 'shortid';
import startCase from 'lodash/startCase';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import { SwitchMode } from '../components/DeviceAsDom/Switch';
import {
  asyncForEach,
  waitFor,
} from '../utils/async';
import {
  getModelUrlForBin,
  initPredictionSession,
  mlConfigUrls,
  mlModelUrls,
} from './batch/free-fall-prediction/free-fall-predition-from-model';
import { PERMISSION } from '../modules/newAuth/model/User';

class DevicesStore {
  @observable
    deviceTypeName = Object.keys(DeviceTypes);
  @observable
    devices = [];
  @observable
    isEditing = false;
  @observable
    editingDeviceId = null;
  @observable
    editingDeviceTypeName = null;
  @observable
    editingDevice = null;
  @observable
    deviceGUIs = [];
  @observable
    guiLoaded = false;
  @observable
    portError = '';
  @observable
    mixerId = null;
  @observable
    binsWithTheSameOutput = [];
  @observable
    linkedPNos = new Map();

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get currentTenantSlug() {
    return this.rootStore.newAuthStore.companyConfig?.slug;
  }

  get materialStore() {
    return this.rootStore.materialStore;
  }

  get batchStore() {
    return this.rootStore.batchStore;
  }

  @computed
  get units() {
    return this.materialStore.unit;
  }

  @computed
  get meteredUnits() {
    return this.materialStore.meteredInventoryUnit.map(v => { return { value: v.id, text: v.symbol || v.name }; });
  }

  @computed
  get weighUnit() {
    return this.materialStore.weighUnit;
  }

  @computed
  get scaleUnits() {
    return this.materialStore.scaleUnits;
  }

  @computed
  get editingDeviceModel() {
    return DeviceModels[this.editingDeviceTypeName];
  }

  @computed
  get sequences() {
    return this.rootStore.dischargeStore.sequences;
  }

  @computed
  get mixerType() {
    return Object.keys(MixerType).map(v => { return { value: MixerType[v], text: startCase(v) }; });
  }

  get mixers() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Mixer) || [];
  }

  get scales() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Scale) || [];
  }

  get finalScales() {
    return this.scales.filter(v => !v.parentScaleId) || [];
  }

  @computed
  get bins() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Bin) || [];
  }

  get pumps() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Pump) || [];
  }

  get diverters() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Diverter) || [];
  }

  get horns() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Horn) || [];
  }

  get boots() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Boot) || [];
  }

  get analogInputs() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.AnalogInput) || [];
  }

  get analogSwitches() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.AnalogSwitch) || [];
  }

  get belts() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Belt) || [];
  }

  get switches() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Switch) || [];
  }

  get manualPanels() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.ManualPanel) || [];
  }

  get scoreboards() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Scoreboard) || [];
  }

  @computed
  get iconDirection() {
    return Object.keys(IconDirection).map(v => { return { value: IconDirection[v], text: startCase(v) }; });
  }

  @computed
  get iconList() {
    return Object.keys(IconList).map(v => { return { value: IconList[v], text: startCase(v) }; });
  }

  @computed
  get iconBinList() {
    return Object.keys(IconBinList).map(v => { return { value: IconBinList[v], text: startCase(v) }; });
  }

  @computed
  get reorderLevelList() {
    return Object.keys(ReorderLevelType).map(v => { return { value: v, text: startCase(ReorderLevelType[v]) }; });
  }

  @computed
  get analogIconList() {
    return Object.keys(AnalogIconList).map(v => { return { value: AnalogIconList[v], text: startCase(v) }; });
  }

  @computed
  get analogUnitList() {
    return Object.keys(AnalogUnitList).map(v => { return { value: v, text: AnalogUnitList[v] }; });
  }

  @computed
  get switchMode() {
    return Object.keys(SwitchMode).map(v => { return { value: v, text: SwitchMode[v] }; });
  }

  @computed
  get doubleGatesOpenMode() {
    return Object.keys(DoubleGatesOpenMode).map(v => { return { value: DoubleGatesOpenMode[v], text: startCase(v) }; });
  }

  @computed
  get secondGateOpenDelayUnit() {
    return Object.keys(SecondGateOpenDelayUnit).map(v => { return { value: SecondGateOpenDelayUnit[v], text: SecondGateOpenDelayUnit[v] }; });
  }

  @computed
  get parity() {
    return Object.keys(Parity).map(v => { return { value: v, text: v }; });
  }

  @computed
  get baudRate() {
    return Object.keys(BaudRate).map(v => { return { value: v, text: v }; });
  }

  @computed
  get displayMode() {
    return Object.keys(DisplayMode).map(v => { return { value: DisplayMode[v], text: v }; });
  }

  @computed
  get vibratorMode() {
    return Object.keys(VibratorMode).map(v => { return { value: VibratorMode[v], text: v }; });
  }

  @computed
  get mixingStartPoint() {
    return Object.keys(MixingStartPoint).map(v => { return { value: v, text: startCase(v) }; });
  }

  setMixerId(mixerId) {
    this.mixerId = mixerId ? Number(mixerId) : null;
  }

  getNextMixer() {
    const { batchStore } = this.rootStore;
    const mixer = this.mixers.find(v => v.diverterPNo) || this.mixers[0];

    if (mixer) {
      return mixer.diverterPNo && batchStore.isOutputOn(mixer.diverterPNo) ? null : mixer.id;
    }

    return null;
  }

  createPortArray = (length = 0) => {
    return Array(length).fill('0').map((v, i) => {
      return {
        value: i+1,
        text: i+1,
      };
    });
  };

  @computed
  get devicesByType() {
    const map = {};
    if (!this.devices) {
      return map;
    }
    (this.deviceTypeName || []).forEach(type => {
      map[type] = this.devices.filter(d => {
        return d && d.deviceTypeName === type;
      });
    });
    return map;
  }

  get decumScaleIds() {
    return this.devices.filter(v => v.deviceTypeName === DeviceTypes.Scale && v.decumulative).map(v => {
      return { value: v.id, name: v.id };
    }) || [];
  }

  getDeviceGUIById = (deviceId, type) => {
    const gui = this.deviceGUIs.find(d => `${d.iconName}` === `${deviceId}`) || {};
    return (
      {
        ...DEFAULT_GUI[type],
        ...gui,
        iconName: `${deviceId}`,
      }
    );
  };

  getPrimaryBin(material) {
    const md =  material ? this.materialStore.materialDetails[material.materialDetailId] : null;

    if (md?.bins) {
      const b = md.bins.find(v => v.isPrimary);
      return b ? this.getDeviceById(b.binId) : null;
    }

    return null;
  }

  getSecondaryBin(material) {
    const md =  material ? this.materialStore.materialDetails[material.materialDetailId] : null;

    if (md?.bins) {
      const b = md.bins.find(v => (v.isPrimary && v.linkedBinId) || (!v.isPrimary && v.isActive));
      if (b?.linkedBinId) {
        return this.getDeviceById(b.linkedBinId);
      } else if (b?.binId) {
        return this.getDeviceById(b.binId);
      }
    }

    return null;
  }

  getBinByBay(material, bay = 0) {
    const md =  material ? this.materialStore.materialDetails[material.materialDetailId] : null;

    if (md && md.bins && md.bins.length && bay) {
      const b = md.bins.find(v => v.bay === Number(bay));
      return b ? this.getDeviceById(b.binId) : null;
    }

    return null;
  }

  getPrimaryBinIdForItemCode(itemCode) {
    const material = this.materialStore.getMaterialByItemCode(itemCode);

    if (!material) {
      return null;
    }

    const b = material.bins?.find(v => v.isPrimary);
    return b?.binId;
  }

  isBinActiveForMultiScale(binId, material) {
    if (!material) {
      return false;
    }

    const bins = this.getActiveBinsForMultiScale(material, true);

    return bins && bins.find(v => v.id === binId);
  }

  getActiveBinsForMultiScale(constituent, isMaterial = false) {
    const { materialStore } = this;
    let material;

    if (isMaterial) {
      material = constituent;
    } else {
      material = constituent.batchCode
        ? materialStore.getMaterialByBatchCode(constituent.batchCode)
        : materialStore.getMaterialByItemId(constituent.constituentItemId);
    }

    let md =  material ? materialStore.materialDetails[material.materialDetailId]: null;

    if (material && material.bins && !md) {
      md = material;
    }

    if (!md || !md.bins) {
      return null;
    }

    let scales = [];
    let bins = [];

    md.bins.filter(v => v.isActive).forEach(v => {
      const bin = this.getDeviceById(v.binId);
      if (bin && bin.scaleId && !scales.includes(bin.scaleId)) {
        scales.push(bin.scaleId);
        bins.push(bin);
      }
    });

    if (scales.length > 1) {
      return bins;
    }

    return null;
  }

  getBinFromMaterial(material, binId) {
    const md =  material ? this.materialStore.materialDetails[material.materialDetailId]: null;

    if (md && md.bins) {
      return md.bins.find(v => v.binId === binId);
    }

    return material.bins.find(v => v.binId === binId);
  }

  getMaterialDetailsForBin(bin) {
    const material = this.materialStore.getMaterialForBin(bin.id);
    return material ? this.materialStore.materialDetails[material.materialDetailId] : null;
  }

  getMaterialTypeForScale = (device, count = 0) => {
    let materialType = '';

    if (!device || device.deviceTypeName !== DeviceTypes.Scale) {
      return '';
    }

    if (count > 5) {
      const error = `Potential configuration issue with scale #${device.id} ${device.description}`;
      logger.warn(error);
      this.rootStore.notifierStore.showWarning({ key: 'Error', props: { error }});
      return '';
    }

    let material = this.materialStore.materials.find(v => v.scaleId === device.id);

    if (material) {
      const materialDetails = material ? this.materialStore.materialDetails[material.materialDetailId] : null;
      return materialDetails ? materialDetails.type : '';
    }

    const bin = this.bins.find(v => v.scaleId === device.id && this.getMaterialDetailsForBin(v));

    if (bin) {
      const md = this.getMaterialDetailsForBin(bin);
      materialType = md.type;
    } else {
      const d = this.scales.find(v => Number(v.parentScaleId) === device.id);
      if (d) {
        return this.getMaterialTypeForScale(d, count + 1);
      }
    }

    return materialType;
  };

  getUnitForScale = scaleId => {
    let unit = '';
    let material = this.materialStore.materials.find(v => v.scaleId === Number(scaleId));

    if (material) {
      const materialDetails = material ? this.materialStore.materialDetails[material.materialDetailId] : null;
      return materialDetails ? materialDetails.itemBatchUnitSymbol : '';
    }

    const bin = this.bins.find(v => v.scaleId === Number(scaleId) && this.getMaterialDetailsForBin(v));

    if (bin) {
      const md = this.getMaterialDetailsForBin(bin);
      unit = md.itemBatchUnitSymbol;
    } else {
      const scale = this.devices.find(v => Number(v.parentScaleId) === scaleId);
      if (scale) {
        return this.getUnitForScale(scale.id);
      }
    }

    return unit;
  };

  getUnitSymbolByUnitId(unitId) {
    return this.materialStore.getUnitSymbolByUnitId(unitId);
  }

  @action
    confirmDuplicateDevice = async (device, deviceType) => {
      const { notifierStore } = this.rootStore;
      const newDevice = { ...device };
      delete newDevice.id;
      newDevice.description = `${device.description} - Copy`;

      try {
        const description = await showConfirmModal({
          title: getI18n().t(`Duplicate ${deviceType}`),
          message: getI18n().t(`Are you sure you want to duplicate this ${deviceType}? All settings will be copied.`),
          okLabel: getI18n().t('Yes'),
          prompt: {
            label: getI18n().t(`New ${deviceType} Name`),
            defaultValue: '',
            minLength: 1,
            maxLength: 15,
            placeholder: getI18n().t(`Enter new ${deviceType} name`),
          },
        });

        newDevice.description = description;
        try {
          const duplicatedDevice = await this.createDevice(deviceType, newDevice);
          return duplicatedDevice;
        } catch (error) {
          const message = `Device ${deviceType} #${device.id} couldn't be duplicated: ${error.message}`;
          notifierStore.showWarning({ key: 'Error', props: { error: message }});
          logger.error(message);
        }
      } catch (error) {
        logger.log(`User didn't confirm duplicating ${deviceType} #${device.id}`);
      }
    };

  getNonDeviceGUIById = id => {
    const gui = this.deviceGUIs.find(d => `${d.iconName}` === `n-${id}`);
    return gui
      ? {
        ...DEFAULT_NON_DEVICE_GUI[id],
        ...gui,
      }
      :
      {
        ...DEFAULT_NON_DEVICE_GUI[id],
        iconName: `n-${id}`,
      };
  };

  getDeviceById = id => {
    return this.devices.find(d => d.id === id);
  };

  @action
    startEdit = (id, type) => {
      id = Number(id);
      this.cancelEdit();

      setTimeout(() => {
        this.editingDeviceId = id;
        this.editingDevice = this.getDeviceById(id);
        this.editingDeviceTypeName = type;
        this.isEditing = true;
      }, 0);
    };

  @action
    cancelEdit = () => {
      this.isEditing = false;
      this.editingDeviceId = null;
      this.editingDeviceTypeName = null;
    };

  setLinkedPNos = () => {
    this.devices.forEach(device => {
      if (device.linkedPNos?.length) {
        device.linkedPNos.forEach(option => {
          if (device[option?.sourceField] && option?.targetPNo) {
            this.linkedPNos.set(device[option.sourceField], option.targetPNo);
          }
        });
      }
    });
  };

  setDevicesByType = () => {
    const types = Object.assign({}, this.devicesByType);
    for (let type in types) {
      if (types.hasOwnProperty(type)) {
        if (!this[`device${type}Ids`]) {
          this[`device${type}Ids`] = [];
        }
        if (types[type].length) {
          const ids = [];
          types[type].forEach(device => {
            const desc = device.description ? device.description : '';
            ids.push({
              value: device.id,
              text: desc ? `${type}: ${desc}` : `${type}: #${device.id}`,
            });
          });
          this[`device${type}Ids`] = ids;
        }
      }
    }
  };

  @action
  filterPorts() {
    const deviceTypes = Object.assign({}, this.devicesByType);
    const portTypes = ['inputPorts', 'outputPorts', 'weightPorts', 'meterPorts', 'analogInputPorts', 'analogOutputPorts'];
    const length = [
      this.batchStore.inputs.length,
      this.batchStore.outputs.length,
      this.batchStore.channels.length,
      this.batchStore.meters.length,
      this.batchStore.analogInputs.length,
      this.batchStore.analogOutputs.length,
    ];
    const map = {};
    this.portError = '';
    this.binsWithTheSameOutput = [];

    portTypes.forEach((type, i) => {
      map[type] = [];
      this[type] = [].concat(this.createPortArray(length[i]));
    });

    Object.keys(DeviceModels).forEach(deviceModelName => {
      const deviceModel = DeviceModels[deviceModelName];
      if (!deviceTypes[deviceModelName]) {
        return;
      }
      deviceTypes[deviceModelName].forEach(device => {
        Object.values(deviceModel).forEach((value, i) => {
          const type = typeof value === 'string' ? value : value.type;
          if (portTypes.indexOf(type) > -1) {
            const port = device[Object.keys(deviceModel)[i]];

            logger.debug(` ${device.deviceTypeName} #${device.id}: port ${port} `, Object.keys(deviceModel)[i], type);

            if (port) {
              if (map[type].indexOf(port) > -1 && port > -1) {
                this.portError += ` ${device.deviceTypeName} #${device.id}: port ${port} used twice! `;
                if (device.deviceTypeName === DeviceTypes.Bin) {
                  this.binsWithTheSameOutput.push({id: device.id, output: port});
                }
                logger.warn(this.portError);
              }
              map[type].push(port);
            }
          }
        });
      });
    });

    portTypes.forEach(type => {
      this[type] = this[type].map(x => {
        return { ...x, text: map[type].includes(x.value) ? x.text : `${x.text} - unused` };
      });
    });

    if (this.portError) {
      logger.warn(this.portError);
    }

    this.checkBinsWithSameOutputs();
  };

  isBinWithTheSameOutput(bin) {
    return this.binsWithTheSameOutput.find(v => v.id === bin.id);
  }

  checkBinsWithSameOutputs() {
    if (!this.binsWithTheSameOutput.length) {
      return;
    }

    let bins = [];

    this.bins.forEach(bin => {
      const b = this.binsWithTheSameOutput.find(v => v.output === bin.gateOnePNo);
      if (b) {
        bins.push({ id: bin.id, output: bin.gateOnePNo });
      }
    });

    this.binsWithTheSameOutput = [ ...bins ];
  }

  getMlModelUrl = async (binId) => {
    const material = this.materialStore.getMaterialForBin(binId);

    if (!material) {
      logger.warn(`Can't fetch ml model. No material for binId found: ${binId}`);
      return;
    }

    const url = `/api/batch/ml-model?plantId=${this.plantId}&binId=${binId}&itemCode=${material.code}`;
    return this.api
      .makeAuthorizedRequest(url)
      .then(async data => {
        if (data?.modelUri && data?.configUri) {
          mlModelUrls.set(binId, data?.modelUri);
          mlConfigUrls.set(binId, data?.configUri);
          try {
            await initPredictionSession(binId);
          } catch (e) {
            logger.warn(e?.stack ?? e?.message ?? String(e));
          }
        } else {
          logger.warn('ml model url missing', binId, data);
        }
      })
      .catch((err) => {
        logger.warn('error fetching ml model for bin:', binId, err?.message);
      });
  };

  getMlModelUrls = async () => {
    if (!this.materialStore.materials.length) {
      await waitFor(3000);
      await this.getMlModelUrls();
      return;
    }

    await asyncForEach(this.devices, async (device) => {
      if (
        device.deviceTypeName === DeviceTypes.Bin
        && !getModelUrlForBin(device.id)
        && device.dynamicFreeFall
        && PERMISSION.BATCH_AI_2
        && !mlModelUrls.has(device.id)
        && !mlConfigUrls.has(device.id)
      ) {
        logger.log('load ml model url', device.id);
        await this.getMlModelUrl(device.id);
        await waitFor(100);
      }
    });
  };

  // devices CRUD
  @action
  loadDevices() {
    const url = `/api/plant/${this.plantId}/device`;
    if (!this.devices.length) {
      this.api.storeDB.init()
        .then(() => this.api.storeDB.getByKey(url))
        .then(data => {
          this.devices = data || [];
          this.setDevicesByType();
          this.setLinkedPNos();
        });
    }

    return this.api
      .makeAuthorizedRequest(url)
      .then(devices => {
        this.devices = devices || [];
        this.setDevicesByType();
        this.setLinkedPNos();
        this.filterPorts();
        this.getMlModelUrls();
      });
  }

  @action
  loadDeviceGUIs(offline = false) {
    const url = `/api/plant/${this.plantId}/gui/icon`;
    if (offline || !this.deviceGUIs.length) {
      this.api.storeDB.init()
        .then(() => this.api.storeDB.getByKey(url))
        .then(data => this.deviceGUIs = data || []);
    }

    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => {
        this.deviceGUIs = resp || [];
      })
      .finally(() => this.guiLoaded = true);
  }

  @action
  upsertDeviceGUI(deviceId, type, params, cb = () => {}) {
    const gui = this.getDeviceGui(deviceId, type, params);
    return this.debouncedSaveGUI(deviceId, gui, cb);
  }

  getDeviceGui(deviceId, type, params) {
    let gui = this.deviceGUIs.find(d => `${d.iconName}` === `${deviceId}`);

    if (!gui) {
      gui = {
        iconName: `${deviceId}`,
        ...DEFAULT_GUI[type],
        ...params,
      };
      this.deviceGUIs.push(gui);
    } else {
      Object.assign(gui, params);
    }

    return gui;
  }

  moveBinsWithTheSameOutput(device, gui) {
    if (device && device.deviceTypeName === DeviceTypes.Bin) {
      this.binsWithTheSameOutput.forEach(b => {
        if (b.output === device.gateOnePNo && b.id !== device.id) {
          const params = { ...gui, iconName: `${b.id}` };
          let guiIndex = this.deviceGUIs.findIndex(d => `${d.iconName}` === `${b.id}`);
          if (guiIndex > -1) {
            this.deviceGUIs[guiIndex] = params;
          }
          this.saveDevicePosition(b.id, params);
        }
      });
    }
  }

  @action
  upsertNonDeviceGUI(id, params, cb = () => {}) {
    if (!this.batchStore.canEmit) {
      cb(params);
      return Promise.resolve();
    }

    let gui = this.deviceGUIs.find(d => `${d.iconName}` === `n-${id}`);

    if (!gui) {
      gui = {
        iconName: `n-${id}`,
        ...DEFAULT_NON_DEVICE_GUI[id],
        ...params,
      };
      this.deviceGUIs.push(gui);
    } else {
      Object.assign(gui, params);
    }

    return this.debouncedSaveGUI(`n-${id}`, { ...DEFAULT_NON_DEVICE_GUI[id], ...gui }, cb);
  }

  @action
    debouncedSaveGUI = (debounce((id, params, cb = () => {}) => {
      return this.saveDevicePosition(id, params).then(() => cb(params));
    }, 500));

  saveDevicePosition(id, params) {
    if (!this.batchStore.canEmit) {
      return Promise.resolve();
    }

    const data = {
      iconName: String(id),
      plantId: this.plantId,
      x: parseInt(params.x, 10),
      y: parseInt(params.y, 10),
      z: parseInt(params.z, 10),
      length: parseInt(params.length, 10),
      size: parseInt(params.size, 10),
      color: params.color,
      background: params.background,
      fontSize: params.fontSize || 14,
    };

    const iconIndex = this.deviceGUIs.findIndex(v => `${v.iconName}` === `${id}`);
    if (iconIndex > -1) {
      this.deviceGUIs[iconIndex] = data;
    }

    return this.api.makeAuthorizedRequest(
      `/api/plant/${this.plantId}/gui/icon/${id}`,
      { ...data },
      'POST'
    )
      .catch(e => this.rootStore.notifierStore.showError(`Issue saving device #${id} position! ${e.message}`));
  }

  @action
  createDevice(deviceTypeName, device) {
    const { isOnline } = this.rootStore.connectionStore;

    if (!isOnline) {
      const createdDevice = { id: shortid.generate(), ...device, deviceTypeName };
      this.devices.push(createdDevice);
      this.saveOffline();
      this.setDevicesByType();
      this.filterPorts();

      return Promise.resolve(createdDevice);
    }

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/${deviceTypeName}`,
        { ...device, plantId: this.plantId },
        'POST'
      )
      .then(newDevice => {
        const createdDevice = { ...device, deviceTypeName, ...newDevice };
        this.devices.push(createdDevice);
        this.saveOffline();
        this.setDevicesByType();
        this.filterPorts();

        return createdDevice;
      });
  }

  saveOffline() {
    this.api.storeDB.init()
      .then(() => this.api.storeDB.save(`/api/plant/${this.plantId}/device`, toJS(this.devices)));
  }

  @action
  updateDevice(deviceId, deviceTypeName, device) {
    const { isOnline } = this.rootStore.connectionStore;

    if (!isOnline) {
      const index = this.devices.findIndex(
        d => d.id === deviceId && d.deviceTypeName === deviceTypeName
      );

      if (index > -1) {
        this.devices[index] = { ...device };
      }

      this.saveOffline();
      this.setDevicesByType();
      this.filterPorts();
    }

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/${deviceTypeName}/${deviceId}`,
        Object.assign(device, { plantId: this.plantId }),
        'PUT'
      )
      .then(updatedDevice => {
        const index = this.devices.findIndex(
          d => d.id === deviceId && d.deviceTypeName === deviceTypeName
        );

        if (index > -1) {
          this.devices[index] = { ...device, ...updatedDevice };
          this.saveOffline();
          this.setDevicesByType();
          this.filterPorts();
        }
      });
  };

  @action
    confirmDeleteDevice = (deviceId, deviceType) => {
      const { notifierStore } = this.rootStore;

      return showConfirmModal({
        title: `Are you sure you want to delete ${deviceType} #${deviceId}?`,
        okLabel: 'Yes',
        cancelLabel: 'No',
        enableEscape: false,
      })
        .then(() => {
          return this.deleteDevice(deviceId)
            .catch(e => {
              const message = `Device ${deviceType} #${deviceId} couldn't be deleted: ${e.message}`;
              notifierStore.showWarning({ key: 'Error', props: { error: message }});
            });
        })
        .catch((_) => logger.log(`User didn't confirm deleting ${deviceType} #${deviceId}`));
    };

  @action
  deleteDevice(deviceId) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/device/${deviceId}`,
        null,
        'DELETE'
      )
      .then(() => {
        this.devices = this.devices.filter(d => d.id !== deviceId);
        this.saveOffline();
        this.setDevicesByType();
        this.filterPorts();
      });
  };

  // Device Enums
  @action
  loadEnumConfiguration(key) {
    return this.api
      .makeAuthorizedRequest(`/api/type/${key}`)
      .then(resp => {
        if (resp) {
          this[key] = resp;
        }
      });
  }

  @action
  loadEnums() {
    // TODO: do we need API to have one request for all types?
    return Promise.all(
      BATCH_TYPE_ENUMS.map(type => !this[type] ? this.loadEnumConfiguration(type) : null)
    );
  }

  async updateMlModel(data) {
    if (!data) {
      return;
    }

    let binId = data.binId;
    if (!binId && data.itemCode) {
      binId = this.getPrimaryBinIdForItemCode(data.itemCode);
    }

    if (!binId) {
      return;
    }

    if (this.batchStore.running?.length) {
      logger.log('ml update wait for batch finish', data);
      this.batchStore.setTimeout(() => {
        this.updateMlModel(data);
      }, 3000);
      return;
    }

    if (data.modelUri) {
      mlModelUrls.set(binId, data.modelUri);
    }

    if (data.configUri) {
      mlConfigUrls.set(binId, data.configUri);
    }

    try {
      await initPredictionSession(binId);
    } catch (e) {
      logger.warn(e?.stack ?? e?.message ?? String(e));
    }
  }

  startWebSockets = () => {
    const { notifierStore, websocketStore, connectionStore, settingsStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.DEVICE_ADD, resp => {
      logger.log('ws:', WebsocketEvent.DEVICE_ADD, resp);

      if (resp) {
        this.loadDevices();
        if (settingsStore.notificationSettings.device) {
          notifierStore.showInfo({
            key: 'Device added!',
            props: { deviceType: resp.deviceTypeName, deviceName: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.DEVICE_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.DEVICE_UPDATE, resp);

      if (resp) {
        this.loadDevices()
          .then(() => {
            if (resp.id === this.editingDeviceId) {
              this.editingDevice = resp;
            }
          });
        if (settingsStore.notificationSettings.device) {
          notifierStore.showInfo({
            key: 'Device updated!',
            props: { deviceType: resp.deviceTypeName, deviceName: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.DEVICE_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.DEVICE_DELETE, resp);

      if (resp) {
        this.loadDevices();
        if (settingsStore.notificationSettings.device) {
          notifierStore.showInfo({
            key: 'Device deleted!',
            props: { deviceType: resp.deviceTypeName, deviceName: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.BIN_AUTO_TUNING, resp => {
      logger.log('ws:', WebsocketEvent.BIN_AUTO_TUNING, resp);

      if (resp?.binIds?.length) {
        if (this.devices.find(v => resp.binIds.includes(v.id))) {
          this.loadDevices();
        }
      }
    });

    this.ws.on(WebsocketEvent.ML_MODEL_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.ML_MODEL_UPDATE, resp);

      if (resp) {
        this.updateMlModel(resp);
      }
    });

    this.ws.onclose(this.startWebSockets);
  };
}

export default DevicesStore;
